import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { GenplanPlot } from '../../model/types';
import { loadCurrentPlots } from '../../api/plotStatuses';
import { analyticsGoals, trackAnalyticsGoal } from '../../../../lib/analytics/yandexMetrika';
import { getPlotPublicNumber } from '../../lib/getPlotPublicNumber';
import { GenplanInfoPanel } from '../GenplanInfoPanel/GenplanInfoPanel';
import { GenplanLegend } from '../GenplanLegend/GenplanLegend';
import styles from './GenplanSection.module.scss';

const GenplanMap = lazy(() => import('./GenplanMapLoader'));

export function GenplanSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [selectedPlotId, setSelectedPlotId] = useState<string>();
  const [activePlotId, setActivePlotId] = useState<string>();
  const [plots, setPlots] = useState<readonly GenplanPlot[]>();
  const [loadError, setLoadError] = useState(false);
  const selectedPlot = plots?.find((plot) => plot.id === selectedPlotId);
  const activePlot = plots?.find((plot) => plot.id === activePlotId);
  const mapHostRef = useRef<HTMLDivElement>(null);
  const [shouldLoadMap, setShouldLoadMap] = useState(
    () =>
      typeof window === 'undefined' ||
      !('IntersectionObserver' in window) ||
      Boolean(getPlotParameter()),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const displayNumber = getPlotParameter();
    if (!displayNumber) return;

    window.requestAnimationFrame(() => {
      const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      sectionRef.current?.scrollIntoView?.({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  }, []);

  useEffect(() => {
    if (!shouldLoadMap) return;
    let cancelled = false;

    const refresh = (force = false) => {
      void loadCurrentPlots({ force })
        .then((currentPlots) => {
          if (cancelled) return;
          setPlots(currentPlots);
          setLoadError(false);
          const number = getPlotParameter();
          setSelectedPlotId(
            (selected) =>
              selected ??
              currentPlots.find((plot) => getPlotPublicNumber(plot.displayNumber) === number)?.id,
          );
        })
        .catch(() => {
          if (!cancelled) setLoadError(true);
        });
    };

    refresh();
    const interval = window.setInterval(() => refresh(true), 30_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [shouldLoadMap]);

  useEffect(() => {
    const mapHost = mapHostRef.current;
    if (shouldLoadMap || !mapHost) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldLoadMap(true);
        observer.disconnect();
      },
      { rootMargin: '700px 0px', threshold: 0 },
    );

    observer.observe(mapHost);
    return () => observer.disconnect();
  }, [shouldLoadMap]);

  const resetSelection = useCallback(() => {
    setSelectedPlotId(undefined);
    updatePlotParameter();
  }, []);

  const selectPlot = useCallback((plot: GenplanPlot) => {
    setSelectedPlotId(plot.id);
    updatePlotParameter(getPlotPublicNumber(plot.displayNumber));
    trackAnalyticsGoal(analyticsGoals.lotDetailsOpen, {
      plot_number: plot.displayNumber,
      plot_status: plot.status.kind,
      with_contractor: plot.status.kind === 'price-per-sotka-with-contractor',
    });
  }, []);

  return (
    <section
      ref={sectionRef}
      className={styles.section}
      id="genplan"
      aria-labelledby="genplan-title"
    >
      <div id="plots" className={styles.anchor} aria-hidden="true" />
      <div className={styles.inner}>
        <div className={styles.header} data-reveal>
          <div className={styles.copy}>
            <p className={styles.eyebrow}>Генплан</p>
            <h2 id="genplan-title">Выберите свой участок</h2>
            <p className={styles.lead}>
              Расположение, площадь и актуальный статус участков на генплане посёлка.
            </p>
          </div>
          <GenplanLegend />
        </div>

        <div ref={mapHostRef} className={styles.workspace}>
          {shouldLoadMap && plots ? (
            <Suspense fallback={<div className={styles.mapPlaceholder}>Генплан загружается…</div>}>
              <GenplanMap
                plots={plots}
                selectedPlotId={selectedPlot?.id}
                activePlot={activePlot}
                onActivePlotChange={(plot) => setActivePlotId(plot?.id)}
                onSelectPlot={selectPlot}
                onResetSelection={resetSelection}
              />
            </Suspense>
          ) : (
            <div className={styles.mapPlaceholder}>
              {loadError
                ? 'Актуальные статусы временно недоступны. Повторим загрузку автоматически.'
                : 'Интерактивный генплан загружается…'}
            </div>
          )}
          <GenplanInfoPanel plot={selectedPlot} />
        </div>
        {loadError && plots && (
          <p role="status">
            Не удалось обновить статусы. Показаны последние полученные данные; обновление повторится
            автоматически.
          </p>
        )}
      </div>
    </section>
  );
}

function getPlotParameter() {
  if (typeof window === 'undefined') return undefined;
  return new URL(window.location.href).searchParams.get('plot')?.trim() || undefined;
}

function updatePlotParameter(displayNumber?: string) {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  if (displayNumber) url.searchParams.set('plot', displayNumber);
  else url.searchParams.delete('plot');

  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}
